import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import type { EmployeeLoan, ApiError } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

function formatCurrency(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatMonthYear(year: number, month: number): string {
  return new Date(year, month - 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}


interface Props {
  loan: EmployeeLoan;
  onClose: () => void;
}

export function RecordLoanPaymentModal({ loan, onClose }: Props) {
  const qc = useQueryClient();

  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(parseFloat(loan.emi_amount).toFixed(0));

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ['loan-payments', loan.id],
    queryFn: () => payrollService.getLoanPayments(loan.id),
  });

  const payEmi = useMutation({
    mutationFn: () => {
      const d = new Date(payDate);
      return payrollService.payEmi(loan.id, {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        amount_paid: Number(amount),
        paid_at: d.toISOString(),
      });
    },
    onSuccess: () => {
      toast.success('EMI recorded');
      void qc.invalidateQueries({ queryKey: ['payroll', 'loans'] });
      void qc.invalidateQueries({ queryKey: ['loan-payments', loan.id] });
      void qc.invalidateQueries({ queryKey: ['loan-deductions', loan.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalAmount = parseFloat(loan.amount);
  const remaining = parseFloat(loan.remaining_amount ?? loan.amount);
  const recovered = totalAmount - remaining;
  const employeeName = [loan.first_name, loan.last_name].filter(Boolean).join(' ') || '—';
  const canPay = payDate.length > 0 && Number(amount) > 0 && !payEmi.isPending;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Record EMI Payment</DialogTitle>
        </DialogHeader>

        {/* Employee + summary */}
        <div className="rounded-lg bg-muted/50 px-4 py-3 space-y-2">
          <p className="text-sm font-medium">Employee: <span className="font-semibold">{employeeName}</span></p>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Total Loan</p>
              <p className="font-semibold">{formatCurrency(loan.amount)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Recovered</p>
              <p className="font-semibold text-emerald-600">{formatCurrency(recovered)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Remaining</p>
              <p className={`font-semibold ${remaining > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {formatCurrency(remaining)}
              </p>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          A payment recorded here replaces that month's scheduled salary deduction.
        </p>

        {/* Form */}
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Payment Date</Label>
            <DatePicker
              value={payDate}
              onChange={(v) => setPayDate(v)}
              placeholder="Pick a date"
            />
          </div>
          <div className="w-36 space-y-1">
            <Label className="text-xs">Amount (₹)</Label>
            <Input
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 5000"
              className="h-9"
            />
          </div>
          <Button
            size="sm"
            className="h-9 shrink-0"
            disabled={!canPay}
            onClick={() => payEmi.mutate()}
          >
            Pay EMI
          </Button>
        </div>

        {/* Paid history */}
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-primary-foreground">
                <th className="px-4 py-2 text-left text-xs font-semibold">Date</th>
                <th className="px-4 py-2 text-right text-xs font-semibold">Amount Paid</th>
                <th className="px-4 py-2 text-right text-xs font-semibold">By</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-muted-foreground">No payments recorded yet</td>
                </tr>
              ) : (
                payments.map((p) => (
                  <tr key={p.id} className="border-t border-border/50">
                    <td className="px-4 py-2.5">
                      {p.paid_at
                        ? new Date(p.paid_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : formatMonthYear(p.year, p.month)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-emerald-600">{formatCurrency(p.amount_paid)}</td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground">
                      {[p.paid_by_first, p.paid_by_last].filter(Boolean).join(' ') || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
